import { useCallback } from 'react';

interface ShareFileOptions {
  url: string;
  filename: string;
  mimeType?: string;
}

interface UseNativeShareResult {
  shareFile: (options: ShareFileOptions) => Promise<boolean>;
  isSupported: boolean;
}

/**
 * Hook for sharing files using the Web Share API.
 * Only enabled on mobile devices - desktop browsers open PDF in new tab instead.
 * Falls back gracefully when not supported.
 */
export function useNativeShare(): UseNativeShareResult {
  const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isSupported = isMobile && !!navigator.share && !!navigator.canShare;

  const shareFile = useCallback(
    async ({ url, filename, mimeType = 'application/pdf' }: ShareFileOptions): Promise<boolean> => {
      if (!isSupported) {
        return false;
      }

      try {
        const response = await fetch(url);
        if (!response.ok) {
          return false;
        }

        const blob = await response.blob();
        const file = new File([blob], filename, { type: mimeType });

        if (!navigator.canShare({ files: [file] })) {
          return false;
        }

        await navigator.share({ files: [file] });
        return true;
      } catch (error) {
        // User cancelled or share failed - fall back to default behavior
        if (error instanceof Error && error.name === 'AbortError') {
          // User cancelled - still consider this "handled" so we don't open a new tab
          return true;
        }
        return false;
      }
    },
    [isSupported],
  );

  return { shareFile, isSupported };
}
